/**
 * Publishes List Page — `/publishes`
 *
 * Index of every Knod the user has published. Search, sort, enable/disable,
 * copy URL, open URL, open in editor, revoke. Read-only summary; editing
 * publish title/description still happens in the per-flow modal in the editor.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Switch from '@mui/material/Switch';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PublicIcon from '@mui/icons-material/Public';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PageHeader from '../components/PageHeader';
import {
  PublishListItem,
  listPublishes,
  updatePublish,
  deletePublish,
  buildPublishUrl,
} from '../services/PublishService';
import { toastService } from '../components/ui/Toast';
import './PublishesListPage.css';

type SortBy = 'updated' | 'created';
type SortOrder = 'asc' | 'desc';

const SORT_BY_KEY = 'publishes-sort-by';
const SORT_ORDER_KEY = 'publishes-sort-order';

const PublishesListPage: React.FC = () => {
  const navigate = useNavigate();

  const [publishes, setPublishes] = useState<PublishListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const [sortBy, setSortBy] = useState<SortBy>(() => {
    const saved = localStorage.getItem(SORT_BY_KEY);
    return saved === 'created' || saved === 'updated' ? saved : 'updated';
  });
  const [sortOrder, setSortOrder] = useState<SortOrder>(() => {
    const saved = localStorage.getItem(SORT_ORDER_KEY);
    return saved === 'asc' || saved === 'desc' ? saved : 'desc';
  });

  // Persist sort prefs
  useEffect(() => {
    localStorage.setItem(SORT_BY_KEY, sortBy);
  }, [sortBy]);
  useEffect(() => {
    localStorage.setItem(SORT_ORDER_KEY, sortOrder);
  }, [sortOrder]);

  // Load data
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listPublishes()
      .then((rows) => {
        if (cancelled) return;
        setPublishes(rows);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[PublishesListPage] load error:', err);
        setError(err.message || 'Failed to load publishes');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let rows = publishes;
    if (q) {
      rows = rows.filter((p) => {
        return (
          (p.title || '').toLowerCase().includes(q) ||
          (p.description || '').toLowerCase().includes(q) ||
          (p.flowName || '').toLowerCase().includes(q)
        );
      });
    }
    const sorted = [...rows].sort((a, b) => {
      const aDate = new Date(sortBy === 'created' ? a.createdAt : a.updatedAt).getTime();
      const bDate = new Date(sortBy === 'created' ? b.createdAt : b.updatedAt).getTime();
      return sortOrder === 'asc' ? aDate - bDate : bDate - aDate;
    });
    return sorted;
  }, [publishes, searchQuery, sortBy, sortOrder]);

  const handleToggleEnabled = async (item: PublishListItem) => {
    const next = !item.enabled;
    setTogglingId(item.id);
    // Optimistic update
    setPublishes((prev) => prev.map((p) => (p.id === item.id ? { ...p, enabled: next } : p)));
    try {
      await updatePublish(item.id, { enabled: next });
      toastService.success(next ? 'Publish enabled' : 'Publish disabled');
    } catch (err: any) {
      console.error('[PublishesListPage] toggle error:', err);
      toastService.error(err.message || 'Failed to update');
      // Roll back
      setPublishes((prev) => prev.map((p) => (p.id === item.id ? { ...p, enabled: !next } : p)));
    } finally {
      setTogglingId(null);
    }
  };

  const handleCopyUrl = async (item: PublishListItem) => {
    try {
      await navigator.clipboard.writeText(buildPublishUrl(item.slug));
      toastService.success('URL copied');
    } catch {
      toastService.error('Failed to copy');
    }
  };

  const handleOpenUrl = (item: PublishListItem) => {
    window.open(buildPublishUrl(item.slug), '_blank');
  };

  const handleOpenEditor = (item: PublishListItem) => {
    navigate(`/knod/${item.flowId}`);
  };

  const handleRevoke = async (item: PublishListItem) => {
    if (!window.confirm(`Revoke publish for "${item.title || item.flowName || 'this Knod'}"? The URL will stop working immediately.`)) {
      return;
    }
    setRevokingId(item.id);
    try {
      await deletePublish(item.id);
      setPublishes((prev) => prev.filter((p) => p.id !== item.id));
      toastService.success('Publish revoked');
    } catch (err: any) {
      console.error('[PublishesListPage] revoke error:', err);
      toastService.error(err.message || 'Failed to revoke');
    } finally {
      setRevokingId(null);
    }
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="publishes-page">
      <PageHeader />
      <main className="publishes-page__main">
        <header className="publishes-page__header">
          <div className="publishes-page__title-row">
            <h1 className="publishes-page__title">
              <PublicIcon sx={{ fontSize: 24, verticalAlign: 'middle', mr: 1, color: 'var(--color-accent-primary)' }} />
              My Publishes
            </h1>
            <span className="publishes-page__count">
              {publishes.length === 0 ? '' : `${publishes.length} ${publishes.length === 1 ? 'publish' : 'publishes'}`}
            </span>
          </div>

          <div className="publishes-page__controls">
            <div className="publishes-page__search">
              <SearchIcon sx={{ fontSize: 18, color: 'var(--color-text-secondary)' }} />
              <input
                type="text"
                placeholder="Search by name, description, or Knod"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="publishes-page__sort">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortBy)}
                aria-label="Sort by"
              >
                <option value="updated">Last updated</option>
                <option value="created">Date created</option>
              </select>
              <button
                type="button"
                className="publishes-page__sort-direction"
                onClick={() => setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))}
                aria-label={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                title={sortOrder === 'asc' ? 'Oldest first' : 'Newest first'}
              >
                {sortOrder === 'asc' ? (
                  <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                ) : (
                  <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                )}
              </button>
            </div>
          </div>
        </header>

        {loading ? (
          <div className="publishes-page__center">
            <CircularProgress sx={{ color: 'var(--color-accent-primary)' }} />
          </div>
        ) : error ? (
          <div className="publishes-page__empty">
            <p>{error}</p>
          </div>
        ) : publishes.length === 0 ? (
          <div className="publishes-page__empty">
            <h2>No published Knods yet</h2>
            <p>
              Open a Knod and click <strong>Publish…</strong> in the toolbar's three-dot menu to share
              it as a public form.
            </p>
            <button
              type="button"
              className="publishes-page__cta"
              onClick={() => navigate('/')}
            >
              Go to Dashboard
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="publishes-page__empty">
            <p>No publishes match "{searchQuery}".</p>
          </div>
        ) : (
          <ul className="publishes-page__list">
            {filtered.map((item) => {
              const url = buildPublishUrl(item.slug);
              return (
                <li key={item.id} className={`publishes-page__row ${item.enabled ? '' : 'is-disabled'}`}>
                  <div className="publishes-page__row-main">
                    <div className="publishes-page__row-title">
                      {item.title || item.flowName || 'Untitled Knod'}
                    </div>
                    <div className="publishes-page__row-meta">
                      <span className="publishes-page__row-url" title={url}>
                        {url}
                      </span>
                      <span className="publishes-page__row-date">
                        Updated {formatDate(item.updatedAt)}
                      </span>
                    </div>
                  </div>

                  <div className="publishes-page__row-actions">
                    <Tooltip title={item.enabled ? 'Disable' : 'Enable'}>
                      <Switch
                        size="small"
                        checked={item.enabled}
                        disabled={togglingId === item.id}
                        onChange={() => handleToggleEnabled(item)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: 'var(--color-accent-primary)',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: 'var(--color-accent-primary)',
                          },
                        }}
                      />
                    </Tooltip>
                    <Tooltip title="Copy URL">
                      <IconButton
                        size="small"
                        onClick={() => handleCopyUrl(item)}
                        sx={{ color: 'var(--color-text-secondary)' }}
                      >
                        <ContentCopyIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Open URL">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenUrl(item)}
                        sx={{ color: 'var(--color-text-secondary)' }}
                      >
                        <OpenInNewIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Open in editor">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEditor(item)}
                        sx={{ color: 'var(--color-text-secondary)' }}
                      >
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Revoke">
                      <IconButton
                        size="small"
                        disabled={revokingId === item.id}
                        onClick={() => handleRevoke(item)}
                        sx={{
                          color: 'var(--color-accent-error)',
                          '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.1)' },
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </div>
  );
};

export default PublishesListPage;
